/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {warn} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {createListenerRegistry} from '@ion/core/src/utils/ListenerRegistry';
import {toNumericIds} from './JobSearchEngineUtils';

/**
 * The one preference key this feature touches. `UserPreferences.set` is
 * `preferences[path] = val` - a flat key inside the app namespace rather than a
 * deep path - so the whole object below goes out on every write and there is no
 * partial-path update to get wrong.
 */
export const WATCHED_JOBS_KEY = 'jobssearch.watchedJobs';

/**
 * One panel row: everything the notification renders, resolved at creation so
 * nothing downstream needs a REST call or a cache lookup.
 */
export interface JobNotificationEntry {
  jobId: number,
  name: string,
  status: string,
  reason?: string,
  occurredAt: string,
  hasSeen: boolean
}

/**
 * The stored blob: the three lists which overlap and are removed from
 * independently, plus the slot reserved for the create-job opt-in.
 *
 * | List | Grows when | Shrinks when |
 * |---|---|---|
 * | `jobIds` | a bell is ticked on | a bell is ticked off, or Unsubscribe from All |
 * | `notifiedJobIds` | a notification is created | pruning to `jobIds` on hydrate |
 * | `notifications` | a notification is created | the user clears it |
 *
 * There is no eviction or expiry, so the blob is unbounded until a limit is
 * decided.
 */
export interface WatchedJobsPreference {
  jobIds: number[],
  notifiedJobIds: number[],
  notifications: JobNotificationEntry[],
  automaticallyWatchCreatedJobs?: boolean
}

const numericIds = (value: unknown): number[] => (
  Array.isArray(value) ? toNumericIds(value).filter(id => id > 0) : []
);

const storedEntries = (value: unknown): JobNotificationEntry[] => (
  Array.isArray(value)
    ? (value as JobNotificationEntry[]).filter(entry => entry && !isNaN(Number(entry.jobId)))
    : []
);

/**
 * Normalises whatever came back out of the preference store into a usable blob.
 *
 * This key is read from stores written by older builds and by the auto-save
 * localStorage restore path, so the shape is never trusted: a `jobIds` which is
 * `undefined` must not throw inside a bell's render.
 *
 * The spread is deliberate and load-bearing. Keys we do not recognise -
 * `automaticallyWatchCreatedJobs` today, whatever is added next - survive a
 * write, which rebuilding the blob from the three lists would silently drop.
 */
const normalise = (stored: unknown): WatchedJobsPreference => {
  const source = (stored && typeof stored === 'object' ? stored : {}) as WatchedJobsPreference;

  return {
    ...source,
    jobIds         : numericIds(source.jobIds),
    notifiedJobIds : numericIds(source.notifiedJobIds),
    notifications  : storedEntries(source.notifications)
  };
};

let blob = normalise(null);

let revision = 0;

const listeners = createListenerRegistry();

const notify = (): void => {
  revision += 1;
  listeners.notify();
};

/**
 * The one write path. Every writer in every surface - toggles, notification
 * creation, panel-open, deletes - reaches the preference store through here.
 *
 * `immediate: true` makes `set` perform the save itself and return its promise,
 * so nobody can forget the `save()` and there is no window between the two.
 *
 * `save()` swallows its own error and returns `Boolean(result?.value)`, so
 * `false` is the failure signal rather than a rejection. It is not always a
 * failure: in read-only mode the write lands in localStorage and `false` comes
 * back on success, which is why nothing here reverts the optimistic value.
 */
const write = async(): Promise<boolean> => {
  const saved = await UserPreferences.set(WATCHED_JOBS_KEY, blob, true);

  if (!saved) {
    warn('[ADM] the watched jobs preference may not have been saved');
  }
  return Boolean(saved);
};

/**
 * Applies one synchronous mutation to the in-memory blob, repaints every
 * listener, and then writes once.
 *
 * The mutation is synchronous by contract, and that is a correctness
 * requirement rather than a style: two mutations either side of an `await`
 * leave a window in which the auto-save can post a half-written store.
 *
 * @param   mutate Invoked with the live blob, which it mutates in place.
 * @returns `true` when the preference store confirmed the write.
 */
export const update = async(mutate: (current: WatchedJobsPreference) => void): Promise<boolean> => {
  mutate(blob);
  notify();
  return write();
};

/**
 * Reads the stored blob once and prunes `notifiedJobIds` to `jobIds`.
 *
 * The prune is what stops a deleted notification reappearing at the next
 * sign-in: an unwatched job's tombstone is worth nothing, since no consumer
 * ever asks about an id outside `jobIds`.
 *
 * > The prune is only safe while a finished job cannot be watched. The bell's
 * > visibility rule - no bell on a finished job the user is not already
 * > watching - is what guarantees that. If any surface ever lets a user watch
 * > an already-finished job, this prune is wrong: the tombstone would have to
 * > outlive the watch rather than be pruned to it.
 */
export const hydrate = (): void => {
  blob = normalise(UserPreferences.get(WATCHED_JOBS_KEY));
  blob.notifiedJobIds = blob.notifiedJobIds.filter(id => blob.jobIds?.includes(id));
  notify();
};

/**
 * The current watch list, in insertion order - oldest watch first.
 */
export const getJobIds = (): number[] => blob.jobIds;

/**
 * The record of jobs we have notified about, for as long as they stay watched.
 * Read by the catch-up query to narrow its ids, so a job which already produced
 * a notification is never asked about - or notified about - twice.
 */
export const getNotifiedJobIds = (): number[] => blob.notifiedJobIds;

/**
 * The panel rows the user can still see.
 */
export const getNotifications = (): JobNotificationEntry[] => blob.notifications;

export const isWatched = (jobId: number): boolean => blob.jobIds?.includes(Number(jobId));

/**
 * Adds or removes one job, repainting every bell for it before the write, so
 * the bell shows what the user clicked with no wait.
 *
 * On a failed write the optimistic value is kept and logged - the retry and
 * revert ladder is deliberately not here yet.
 */
export const toggleWatch = async(jobId: number): Promise<void> => {
  const id = Number(jobId);

  await update(current => {
    current.jobIds = current.jobIds?.includes(id)
      ? current.jobIds.filter(watched => watched !== id)
      : current.jobIds.concat(id);
  });
};

/**
 * Adds one job to the watch list, and does nothing at all when it is already
 * there. The create-job path needs add-only: `toggleWatch` on an id which is
 * already watched would silently unwatch it.
 *
 * The already-watched case deliberately does not write. A no-op write would
 * still bump the revision and repaint every bell for nothing.
 */
export const watch = async(jobId: number): Promise<void> => {
  const id = Number(jobId);

  if (id && !isNaN(id) && !isWatched(id)) {
    await update(current => {
      current.jobIds = current.jobIds.concat(id);
    });
  }
};

/**
 * Whether newly created jobs should be watched without being asked.
 *
 * Reads the in-memory blob, so it is only meaningful after `hydrate()` - which
 * `initializePreferences` runs before anything can render a dialog. A blob
 * written by an older build has no such key, hence the `false` default.
 */
export const getWatchCreatedJobsDefault = (): boolean => (
  blob.automaticallyWatchCreatedJobs ?? false
);

/**
 * Stores the create-job default.
 *
 * Written from two surfaces - the Create Job banner and the profile
 * preferences row - and both come through here, which is what keeps this
 * module the blob's only writer. `UserPreferences.set` mutates the store in
 * place, so the profile dialog's own batched save and this write do not clobber
 * one another; if either ever rebuilds the store from a snapshot, they will.
 *
 * See the note on `write`: `false` is not proof of failure - in read-only mode
 * a successful localStorage write returns it - so no caller may revert on it.
 *
 * @returns `true` when the preference store confirmed the write.
 */
export const setWatchCreatedJobsDefault = async(on: boolean): Promise<boolean> => (
  update(current => {
    current.automaticallyWatchCreatedJobs = on;
  })
);

/**
 * Empties the watch list in one write - the Watching tab's bulk action.
 *
 *  **`notifiedJobIds` is deliberately left alone.** It is pruned to `jobIds`
 * at the next hydrate instead: clearing the tombstones here would let every
 * finished job re-notify if it were ever re-watched in this session.
 */
export const unwatchAll = async(): Promise<void> => {
  await update(current => {
    current.jobIds = [];
  });
};

export const subscribe = (listener: () => void): (() => void) => (
  listeners.subscribe(listener)
);

/**
 * A counter bumped on every mutation, for the hooks to depend on.
 */
export const version = (): number => revision;

/**
 * Clears the module. Module state outlives a test case, so this exists for test
 * isolation - never call it from application code.
 */
export const reset = (): void => {
  blob = normalise(null);
  revision = 0;
  listeners.clear();
};
